import React, { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'ra-core';
import { Create, FileInput, FunctionField, SimpleForm, TextInput, Toolbar } from 'react-admin';
import { useForm } from 'react-final-form';
import AUDIT from "../model/InboundAudit";
import DownloadIcon from "@material-ui/icons/GetApp";
import MatButton from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { HiddenInputCsrfToken } from "./HiddenInputCsrfToken";
import { hasFieldData } from "../validation/fieldValidation";
import { validateTextFile } from "../validation/textfileValidation";

// Must match PortfolioController.maxRequest - the server silently exports only this many ISINs
const PORTFOLIO_EXPORT_LIMIT = 40_000;

// Mirrors PortfolioService.extractIsinStream: a run of exactly 12 characters from A-Z/0-9 is one ISIN
function countIsins(text: string): number {
    return text.split(/[^A-Z0-9]+/).filter(token => token.length === 12).length;
}

async function countPortfolioIsins(values): Promise<number> {
    const fileText = values.file?.rawFile ? await values.file.rawFile.text() : '';
    return countIsins(fileText) + countIsins(values.pasted || '');
}

/**
 * Ugly difference - we expect Portfolio requests to appear only from the GUI.
 * Unlike other APIs it uses vanilla HTML5 form upload which returns a portfolio response
 * in the same request as a downloadable file.
 */
export function submitPortfolioUpload() {
    const form = document.forms["portfolioOneOffUpload"]
    if (!form) return Promise.reject("Portfolio download works only from the portfolio screen");

    // Start the upload process
    form.submit();

    // It's pretty much impossible to detect when FORM upload is finished, or download started
    // There is a hack with a cookie to identify when download has finished, but here we assume that
    // it will take 2 seconds for downloaded file to appear in the browser. Complete download will take time
    const fakeButtonDelay = 2;

    // Using Promise to keep Submit button locked (busy with spinner icon) for 2 seconds
    return new Promise((res) => {
            // @ts-ignore
            setTimeout(() => res({ data: { id: 0 } }), 1000 * fakeButtonDelay)
        }
    )
}

function PostCreateToolbar(props) {
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const form = useForm();

    const [limitDialogOpen, setLimitDialogOpen] = useState(false);
    const [isinCount, setIsinCount] = useState(0);
    const [saving, setSaving] = useState(false);
    const [pendingValues, setPendingValues] = useState(undefined);

    // A dropped file lands in form state only; the native input that the HTML form posts stays empty.
    // Copy the selected file onto that input so the upload includes it, whether it was picked or dropped.
    function attachSelectedFile(values) {
        const rawFile = values.file?.rawFile;
        const input = document.querySelector<HTMLInputElement>('#portfolioOneOffUpload input[type="file"]');
        if (!rawFile || !input) return;
        const transfer = new DataTransfer();
        transfer.items.add(rawFile);
        input.files = transfer.files;
    }

    function submitExport(values) {
        setSaving(true);
        attachSelectedFile(values);
        submitPortfolioUpload()
            .then(() => {
                notify('resources.portfolio.started');
                // erase the form
                refresh();
            })
            .catch(() => notify('ra.notification.http_error', 'warning'))
            .finally(() => setSaving(false));
    }

    function onExportClick() {
        const state = form.getState();
        // Let react-final-form show the validation messages
        if (!state.valid) {
            form.submit();
            return;
        }
        countPortfolioIsins(state.values).then(count => {
            if (count > PORTFOLIO_EXPORT_LIMIT) {
                setIsinCount(count);
                setPendingValues(state.values);
                setLimitDialogOpen(true);
            } else {
                submitExport(state.values);
            }
        });
    }

    return <Toolbar {...props}>

        <MatButton variant="contained" color="primary" startIcon={<DownloadIcon/>} onClick={onExportClick} disabled={saving}>
            {translate('ra.action.export')}
        </MatButton>

        <Dialog
            open={limitDialogOpen}
            onClose={() => setLimitDialogOpen(false)}
            aria-labelledby="portfolio-limit-dialog-title"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="portfolio-limit-dialog-title" style={{ color: '#d32f2f' }}>{"Portfolio exceeds export limit"}</DialogTitle>
            <DialogContent>
                <Typography variant="body1">
                    {`Your portfolio contains ${isinCount.toLocaleString()} ISINs. The export is limited to ${PORTFOLIO_EXPORT_LIMIT.toLocaleString()} ISINs, so only the first ${PORTFOLIO_EXPORT_LIMIT.toLocaleString()} will be included in the file.`}
                </Typography>
            </DialogContent>
            <DialogActions>
                <MatButton onClick={() => setLimitDialogOpen(false)} color="primary">
                    Cancel
                </MatButton>
                <MatButton onClick={() => { setLimitDialogOpen(false); submitExport(pendingValues); }} color="primary" variant="contained" autoFocus>
                    Export first {PORTFOLIO_EXPORT_LIMIT.toLocaleString()}
                </MatButton>
            </DialogActions>
        </Dialog>

    </Toolbar>;
}

function validateAll(values) {
    const errors = {} as any;
    if (!hasFieldData(values.file) && !values.pasted) {
        errors.file = "resources.portfolio.emptyForm";
    }

    return errors
}

export function PortfolioUpload(props) {
    return <Create {...props} title="Portfolio Export">

        <SimpleForm toolbar={<PostCreateToolbar/>}
                    id="portfolioOneOffUpload"
                    encType="multipart/form-data"
                    action="/anna/api/v1/portfolio/export"
                    method="POST"
                    download={true}
                    validate={validateAll}
        >
            {/*
              * This SimpleForm is backed by a usual HTML form, and a normal classical POST request is be used
              * to upload a file (or pasted data) when [Export] button is clicked.
              * Server responds with OK 200 and provides the file for download
             */}
            <HiddenInputCsrfToken/>


            <FileInput
                source={AUDIT.file}
                fullWidth
                type="file"
                validate={validateTextFile}
                helperText="resources.portfolio.hints.file">

                <FunctionField source={AUDIT.file} // used for sorting
                               label={AUDIT.file}
                               render={file => {
                                   return file.rawFile.name + " is selected";
                               }}
                />

            </FileInput>

            <TextInput multiline
                       source="pasted"
                       fullWidth
                       helperText="resources.portfolio.hints.pasted"
                       rowsMax={5}
            />

        </SimpleForm>
    </Create>
}
